// src/pages/admin/DepositMatchRow.jsx
import { daysBetween, fmtShortDay, formatCad, formatSignedCad } from "./depositMatchUtils";
import { CATEGORY_LABELS } from "./depositAutoMatch";

// Deposit | status | bank | actions — shared with the panel's sticky header so
// the columns line up.
export const MATCH_GRID =
  "grid grid-cols-[minmax(0,1fr)_128px_minmax(0,1fr)_150px] items-center gap-x-3";

const STATUS = {
  matched: {
    label: "Matched",
    pill: "border-palette-emerald-ring bg-palette-emerald-muted text-palette-emerald-hover",
  },
  variance: {
    label: "Variance",
    pill: "border-palette-amber-label bg-palette-amber-muted text-palette-amber-primary",
  },
  depositPending: {
    label: "Not in bank",
    pill: "border-palette-slate-ring bg-palette-slate-muted text-palette-slate-secondary",
  },
  suggested: {
    label: "Suggested",
    pill: "border-palette-indigo-border bg-palette-indigo-soft text-palette-indigo-badge",
  },
  excluded: {
    label: "Excluded",
    pill: "border-palette-slate-divider bg-white text-palette-slate-placeholder",
  },
  bankPending: {
    label: "No deposit",
    pill: "border-palette-amber-label bg-palette-amber-muted text-palette-amber-primary",
  },
};

// Per-side styling: the tag chip, the date colour and the card border.
const SIDES = {
  deposit: {
    tag: "Deposit",
    tagClass: "border-palette-slate-divider bg-palette-slate-muted text-palette-slate-heading",
    dateClass: "text-palette-slate-heading",
    cardBorder: "border-palette-slate-divider",
  },
  bank: {
    tag: "Bank",
    tagClass: "border-palette-emerald-border bg-palette-emerald-soft text-palette-emerald-primary",
    dateClass: "text-palette-emerald-hover",
    cardBorder: "border-palette-emerald-border",
  },
};

function LineCard({ side, date, aside, title, name, amount, selectable, selected, onToggle }) {
  const s = SIDES[side];
  const Tag = selectable ? "label" : "div";
  return (
    <Tag
      className={`flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg border bg-white py-3.5 pl-3 pr-4 shadow-sm transition-colors ${
        selected ? "border-palette-indigo-label ring-2 ring-palette-indigo-ring" : s.cardBorder
      } ${selectable ? "cursor-pointer hover:border-palette-indigo-border" : ""}`}
    >
      <div className="flex min-w-0 flex-[0_1_auto] items-center gap-2">
        {selectable && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggle}
            className="h-4 w-4 flex-none rounded accent-palette-indigo-badge"
            aria-label={`Select ${s.tag.toLowerCase()} line ${name}`}
          />
        )}
        <span
          className={`whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-bold uppercase leading-tight tracking-wider ${s.tagClass}`}
        >
          {s.tag}
        </span>
        <span
          className={`whitespace-nowrap text-xs font-semibold uppercase tracking-wider tabular-nums ${s.dateClass}`}
        >
          {fmtShortDay(date)}
        </span>
        {aside && (
          <span className="min-w-0 truncate whitespace-nowrap text-xs text-palette-slate-placeholder">
            {aside}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-[1_1_170px] items-baseline gap-2">
        <span
          title={title}
          className="min-w-0 truncate text-sm font-semibold text-palette-slate-primary"
        >
          {name}
        </span>
        <span className="ml-auto flex-none whitespace-nowrap text-sm font-semibold tabular-nums text-palette-slate-primary">
          {formatCad(amount)}
        </span>
      </div>
    </Tag>
  );
}

// "N days later" against the row's earliest deposit, when it has one.
const lagLabel = (deposits, bank) => {
  const first = deposits.reduce((min, d) => (!min || d.date < min ? d.date : min), null);
  if (!first) return null;
  const d = daysBetween(first, bank.date);
  if (d === 0) return "Same day";
  return `${d} ${d === 1 ? "day" : "days"} later`;
};

/**
 * DepositMatchRow — Collection Breakdown line(s) and the bank line(s) they pair
 * with, as four columns: deposit cards · status · bank cards · actions. A saved
 * manual match can hold several lines on each side; they stack.
 *
 *   deposits: [{ id, date, method, amount, ref?, title? }]
 *   banks:    [{ id, date, description, amount, title? }]
 *   status:   "matched" | "variance" | "depositPending" | "bankPending"
 *   variance: bank − deposit, shown under the pill when non-zero
 *   A side with no lines renders as an empty cell — only real lines are shown.
 *   selectable: pending lines get a checkbox; `isSelected(side, line)` and
 *     `onToggle(side, line)` drive it
 *   onUnmatch: set on saved matches — renders the Unmatch button
 */
export default function DepositMatchRow({
  deposits = [],
  banks = [],
  status,
  variance = 0,
  selectable = false,
  isSelected,
  onToggle,
  onUnmatch,
  unmatching = false,
  manual = false,
  // Saved by the page on its own (an exact-amount suggestion).
  auto = false,
  // Suggested rows: the rule's label, its confidence, and Accept.
  label,
  confidence,
  onAccept,
  accepting = false,
}) {
  const s = STATUS[status] || STATUS.depositPending;
  const card = (side, line, props) => (
    <LineCard
      key={line.id}
      side={side}
      date={line.date}
      selectable={selectable}
      selected={selectable && isSelected?.(side, line)}
      onToggle={() => onToggle?.(side, line)}
      {...props}
    />
  );

  return (
    <div
      className={`${MATCH_GRID} border-t border-palette-slate-divider bg-palette-slate-soft px-4 py-2.5`}
    >
      {deposits.length > 0 ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          {deposits.map((d) =>
            card("deposit", d, { aside: d.ref, title: d.title, name: d.method, amount: d.amount }),
          )}
        </div>
      ) : (
        <div />
      )}

      <div className="flex flex-col items-center justify-center gap-1">
        <span
          className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[11.5px] font-semibold ${s.pill}`}
        >
          {s.label}
        </span>
        {variance !== 0 && (
          <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-palette-amber-primary">
            {formatSignedCad(variance)}
          </span>
        )}
        {status === "suggested" && label && (
          <span
            className={`whitespace-nowrap text-[11px] font-semibold ${
              confidence === "exact" ? "text-palette-indigo-badge" : "text-palette-amber-primary"
            }`}
          >
            {label}
          </span>
        )}
        {status === "excluded" && (
          <span className="whitespace-nowrap text-[11px] text-palette-slate-placeholder">
            Not patient money
          </span>
        )}
        {(manual || auto) && (
          <span className="whitespace-nowrap text-[11px] text-palette-slate-placeholder">
            {auto ? "Auto" : "Manual"} · {deposits.length}:{banks.length}
          </span>
        )}
      </div>

      {banks.length > 0 ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          {banks.map((b) =>
            card("bank", b, {
              aside: [CATEGORY_LABELS[b.category], lagLabel(deposits, b)]
                .filter(Boolean)
                .join(" · "),
              title: b.title,
              name: b.description,
              amount: b.amount,
            }),
          )}
        </div>
      ) : (
        <div />
      )}

      <div className="flex justify-end gap-0.5 pl-1">
        {onAccept && (
          <button
            type="button"
            onClick={onAccept}
            disabled={accepting}
            title="Save this as a match"
            className="h-[30px] whitespace-nowrap rounded-md border border-palette-slate-divider bg-white px-3 text-[12.5px] font-medium text-palette-emerald-hover transition-colors hover:border-palette-emerald-border hover:bg-palette-emerald-soft disabled:opacity-50"
          >
            {accepting ? "Saving…" : "Accept"}
          </button>
        )}
        {onUnmatch && (
          <button
            type="button"
            onClick={onUnmatch}
            disabled={unmatching}
            title="Unlink these lines — they all become pending again"
            className="h-[30px] whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-semibold text-palette-slate-secondary transition-colors hover:bg-palette-slate-muted disabled:opacity-50"
          >
            {unmatching ? "Unmatching…" : "Unmatch"}
          </button>
        )}
      </div>
    </div>
  );
}
